/**
 * FeedbackJournal — the continuously-growing diary of every Iqra-captured
 * screenshot + comment from across the platform. Admin reads it like a
 * book: oldest entries at the top, newest at the bottom, each entry
 * showing the page, the user's note, screenshots inline.
 *
 * Renders a single concatenated document (not a card grid) so the admin
 * can scroll through it in chronological order — closest analogue is a
 * stenographer's transcript of every user moment.
 *
 * Action: triage status from inline pills + export the whole journal as
 * Markdown.
 */
import React, { useEffect, useState } from 'react';
import {
  BookOpen, RefreshCcw, Download, Loader2, ImageOff,
  CheckCircle2, Image as ImageIcon, ExternalLink,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const SEV_TONE = {
  P0: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  P1: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  P2: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  P3: 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70',
};

const STATUS_NEXT = ['new', 'triaged', 'accepted', 'in_progress', 'done', 'rejected'];

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
export default function FeedbackJournal() {
  const [entries, setEntries] = useState([]);
  const [markdown, setMarkdown] = useState('');
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [includeShots, setIncludeShots] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/iqra/journal', {
        params: { include_screenshots: includeShots, limit: 500 },
      });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setMarkdown(data?.markdown || '');
      setCount(data?.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load journal.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [includeShots]);

  const updateStatus = async (cr_id, status) => {
    try {
      await api.patch(`/iqra/change-requests/${cr_id}`, { status });
      toast.success(`Marked ${status.replace('_', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    }
  };

  const downloadMarkdown = () => {
    const blob = new Blob([markdown || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vigilant-feedback-journal-${new Date().toISOString().slice(0,10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ve-section" data-testid="feedback-journal-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <BookOpen size={14} className="text-[#8B3FC5]"/> Feedback Journal · Iqra
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold text-slate-900 dark:text-white">
            One continuous diary. <span className="ve-gold-text">Every voice. Every screenshot.</span>
          </h1>
          <p className="text-sm text-slate-600 dark:text-white/70 mt-2 max-w-2xl leading-relaxed">
            Every note + screenshot a user logs through Iqra appends here — oldest at the top, newest at the bottom — forming a single growing record of what the platform learned from its users. Admin-only.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 dark:text-white/65 inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeShots}
              onChange={(e) => setIncludeShots(e.target.checked)}
              data-testid="journal-toggle-shots"
              className="accent-[#8B3FC5]"
            />
            Include screenshots
          </label>
          <button
            data-testid="journal-refresh"
            onClick={load}
            className="ve-btn-secondary inline-flex items-center gap-2 text-xs"
          >
            <RefreshCcw size={13}/> Refresh
          </button>
          <button
            data-testid="journal-download"
            onClick={downloadMarkdown}
            className="ve-btn-primary inline-flex items-center gap-2 text-xs"
          >
            <Download size={13}/> Download .md
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
        <Stat n={count} l="Entries"/>
        <Stat n={entries.filter((e) => e.screenshot_data_url).length} l="With screenshots"/>
        <Stat n={entries.filter((e) => (e.severity || 'P2') === 'P0' || e.severity === 'P1').length} l="High severity"/>
        <Stat n={entries.filter((e) => (e.status || 'new') === 'new').length} l="Untriaged"/>
      </div>

      {/* The concatenated journal */}
      <div className="mt-10 ve-card p-6 md:p-10" data-testid="journal-body">
        {loading && (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Loading journal…
          </div>
        )}
        {!loading && entries.length === 0 && (
          <div className="text-center py-12">
            <BookOpen size={26} className="mx-auto text-slate-300 dark:text-white/20 mb-3"/>
            <div className="text-sm font-bold text-slate-700 dark:text-white/85">The journal is empty.</div>
            <div className="text-xs text-slate-500 dark:text-white/55 mt-1">
              Capture a screenshot through the Iqra drawer to log your first entry.
            </div>
          </div>
        )}

        {!loading && entries.map((e, i) => (
          <JournalEntry key={e.change_request_id} entry={e} index={i + 1} onStatus={updateStatus}/>
        ))}
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.03] p-4">
      <div className="display text-2xl font-bold ve-gold-text leading-none">{n}</div>
      <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">{l}</div>
    </div>
  );
}

function JournalEntry({ entry, index, onStatus }) {
  const sev = entry.severity || 'P2';
  const status = entry.status || 'new';
  const ts = new Date(entry.created_at);
  return (
    <article
      data-testid={`journal-entry-${entry.change_request_id}`}
      className="relative border-l-2 border-l-[#8B3FC5]/50 pl-6 pr-2 py-6 mb-2"
    >
      {/* Timeline dot */}
      <span
        className="absolute -left-[5px] top-7 h-2.5 w-2.5 rounded-full bg-[#8B3FC5] ring-4 ring-white dark:ring-[#0d0d0d]"
        aria-hidden="true"
      />

      {/* Title row */}
      <header className="flex flex-wrap items-baseline gap-3">
        <span className="mono text-[10px] text-slate-400 dark:text-white/35 tracking-wider">
          #{String(index).padStart(3, '0')} · {ts.toLocaleString()}
        </span>
        <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] ${SEV_TONE[sev]}`}>{sev}</span>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70">
          {entry.classification || 'other'}
        </span>
      </header>

      {/* Title */}
      {entry.title && (
        <h3 className="display text-lg font-bold mt-2 leading-snug text-slate-900 dark:text-white">{entry.title}</h3>
      )}

      {/* Source line */}
      <div className="mt-1.5 text-[11px] text-slate-500 dark:text-white/55 flex flex-wrap gap-x-3 gap-y-1">
        <span>{entry.submitted_by_email}</span>
        <span className="text-slate-400">·</span>
        <span className="capitalize">{(entry.submitted_by_role || '').replace(/_/g, ' ')}</span>
        {entry.page_url && (
          <>
            <span className="text-slate-400">·</span>
            <span className="mono text-slate-400 inline-flex items-center gap-1">
              <ExternalLink size={10}/> {entry.page_url}
            </span>
          </>
        )}
      </div>

      {/* User's words */}
      {entry.user_text && (
        <blockquote className="mt-3 pl-4 border-l-2 border-l-slate-200 dark:border-l-white/15 text-sm text-slate-700 dark:text-white/80 leading-relaxed whitespace-pre-wrap">
          {entry.user_text}
        </blockquote>
      )}

      {/* Iqra's summary */}
      {entry.summary && (
        <p className="mt-3 text-sm text-slate-600 dark:text-white/65 leading-relaxed">
          <span className="text-[#5E1F8B] dark:text-[#8B3FC5] text-[10px] uppercase tracking-[0.22em] font-bold mr-2">Iqra summary:</span>
          {entry.summary}
        </p>
      )}

      {/* Acceptance criteria */}
      {entry.acceptance_criteria?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {entry.acceptance_criteria.map((a, i) => (
            <li key={i} className="text-xs text-slate-600 dark:text-white/65 leading-snug">
              <span className="text-[#5E1F8B] dark:text-[#8B3FC5] font-bold mr-1">›</span>{a}
            </li>
          ))}
        </ul>
      )}

      {/* Screenshot inline */}
      {entry.screenshot_data_url ? (
        <figure className="mt-4">
          <img
            src={entry.screenshot_data_url}
            alt="Captured at submission"
            className="rounded-lg border border-slate-200 dark:border-white/10 max-w-full max-h-[420px] object-contain"
          />
          <figcaption className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/40 inline-flex items-center gap-1.5">
            <ImageIcon size={10}/> Screenshot at {ts.toLocaleTimeString()}
          </figcaption>
        </figure>
      ) : (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/35">
          <ImageOff size={10}/> No screenshot attached
        </div>
      )}

      {/* Status pills */}
      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/55 mr-1">Status:</span>
        {STATUS_NEXT.map((s) => {
          const active = status === s;
          return (
            <button
              key={s}
              onClick={() => !active && onStatus(entry.change_request_id, s)}
              data-testid={`journal-status-${entry.change_request_id}-${s}`}
              disabled={active}
              className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] transition
                ${active
                  ? 'bg-[#0A0A0A] text-[#8B3FC5] ring-1 ring-[#8B3FC5]/40 cursor-default'
                  : 'border border-slate-200 dark:border-white/10 text-slate-600 dark:text-white/65 hover:border-[#8B3FC5]/60'}`}
            >
              {active && <CheckCircle2 size={9} className="inline mr-1"/>}
              {s.replace('_', ' ')}
            </button>
          );
        })}
      </div>

      {/* Admin notes */}
      {entry.admin_notes && (
        <p className="mt-3 text-xs italic text-slate-500 dark:text-white/55 leading-relaxed">
          <span className="not-italic font-bold mr-1">Admin:</span> {entry.admin_notes}
        </p>
      )}
    </article>
  );
}
